"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  dateToRegnal,
  regnalToDates,
} from "@/lib/tool-kit/legal-styles/regnal-results";
import {
  type DateFormat,
  formatLegalDay,
  REIGNS,
  regnalYear,
  reignSpan,
  type YearNote,
} from "@/lib/tool-kit/legal-styles/regnal-years";

type Mode = "toDates" | "toYear";
type CalendarMode = "england" | "gregorian";

/** English strings. A translated content file overrides these through `ui`. */
export const REGNAL_YEAR_CONVERTER_STRINGS = {
  modeLabel: "Convert",
  modeToDates: "A regnal year to dates",
  modeToYear: "A date to a regnal year",
  citationLabel: "Regnal year citation",
  citationPlaceholder: "13 Cha. 2",
  citationHint:
    "The number and the monarch as an Act or record gives them: 41 Geo. 3, 1 Will. & Mar., or 13 & 14 Cha. 2 for a session that crosses two years.",
  dateLabel: "Date",
  datePlaceholder: "1661-01-30",
  dateHint:
    "Year-month-day, 30 January 1661, 30/1/1661, or an old-style date such as 30 January 1660/61.",
  calendarLabel: "Calendar the date is written in",
  calendarEngland:
    "As England wrote it: Julian before 14 September 1752, Gregorian after",
  calendarGregorian: "Gregorian (the calendar in use today)",
  tryExample: "Try an example",
  clear: "Clear",
  exampleCitation: "13 Cha. 2",
  exampleDate: "15 June 1215",
  resultsHeading: "Result",
  emptyPromptToDates: "Enter a regnal year such as 13 Cha. 2 to see its dates.",
  emptyPromptToYear: "Enter a date to see its regnal year.",
  copy: "Copy",
  copied: "Copied!",
  regnalYear: "Regnal year",
  withoutStops: "Without full stops (OSCOLA)",
  calendarYears: "Calendar years",
  reign: "Reign",
  startsLabel: "Begins",
  endsLabel: "Ends",
  dayLabel: "The date you entered",
  gregorianLabel: "Gregorian equivalent",
  summary: "{citation}: {start} to {end}",
  summaryDay: "{day} falls in {citation}, which runs from {start} to {end}",
  oldStyle: "{date} (Old Style)",
  equals: "{written} = {gregorian}",
  datePattern: "{day} {month} {year}",
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  partialYear:
    "This year is shorter than twelve months because the reign began or ended inside it.",
  sessionNote:
    "An Act is cited by the regnal year or years of the session in which it passed, not the year of its royal assent, so the year it was passed in can differ from the year you need. Look up the session before you cite.",
  oldStyleNote:
    "Dates before 14 September 1752 are Julian (Old Style), as England wrote them. Until 1751 the legal year began on 25 March, so January to March dates carry two years: 1660/61.",
  errorFormat:
    "We could not read that. Write a number and a monarch, such as 13 Cha. 2, 41 Geo. 3 or 1 Will. & Mar.",
  errorReign:
    "We do not know a reign called “{name}”. Try Hen. 8, Eliz. 1, Jac. 1, Cha. 2, Ann., Geo. 3, Vict. or one of the abbreviations in the table below.",
  errorRange:
    "{reign} has no such regnal year: its years run from {first} to {last}.",
  errorDateFormat:
    "We could not read that date. Try 1661-01-30, 30 January 1661 or 30/1/1661.",
  errorInvalid: "That date does not exist in the calendar you chose.",
  errorLost:
    "The days from 3 to 13 September 1752 never existed in England: 2 September was followed by 14 September.",
  errorDual:
    "A double year such as 1660/61 only fits a date from 1 January to 24 March before 1752 in the Julian calendar.",
  errorBefore:
    "English regnal years begin with William I in October 1066, so there is no regnal year for an earlier date.",
  interregnum:
    "No regnal year: by the usual reckoning this day falls between two reigns.",
  notes: {
    janeGrey:
      "Lady Jane Grey was proclaimed queen from 10 to 19 July 1553, but the official record counts Mary's reign from 6 July 1553.",
    readeption:
      "Henry VI was briefly restored from 9 October 1470 to 11 April 1471. Edward IV's regnal years are counted straight through it; documents of the restoration used Henry's own year.",
    commonwealth:
      "Charles II's years are counted from 30 January 1649, the day his father was executed, although he was restored only on 29 May 1660. Documents written during the Commonwealth were not dated by his reign.",
    sharedDay:
      "One reign ended and the next began on this day. The new monarch's regnal year begins on it.",
    edwardIiiFrance:
      "From 25 January 1340 Edward III's documents also carry a separate year of his reign in France.",
    earlyReign:
      "Reigns before Henry III (1216) are counted from accession dates that sources give differently, and statutes are not cited by them. Treat the result as a reference point.",
    ascension:
      "John's regnal years begin on Ascension Day, which moves from year to year, so each one starts on a different date.",
    williamIii:
      "William III's years carry on the numbering begun for William and Mary on 13 February 1689.",
    philipMary:
      "Philip and Mary's years are cited in pairs: 1 & 2 Phil. & Mar. is the first year, from 25 July 1554.",
  },
  tableHeading: "Reigns and abbreviations",
  tableSummary: "Show every reign, its abbreviation and its first regnal year",
  tableReign: "Reign",
  tableAbbr: "Abbreviation",
  tableFirstYear: "Regnal year 1 begins",
  tableLastDay: "Reign ends",
  reignYearsHeading: "Every regnal year of this reign",
  reignYearsSummary: "Show every year of {reign}",
  tableYear: "Year",
  ongoing: "ongoing",
} as const;

type Strings = typeof REGNAL_YEAR_CONVERTER_STRINGS;

function dateFormat(strings: Strings): DateFormat {
  return {
    months: strings.months,
    pattern: strings.datePattern,
    oldStyle: strings.oldStyle,
    equals: strings.equals,
  };
}

function errorText(
  strings: Strings,
  error:
    | { reason: "format" }
    | { reason: "reign"; name: string }
    | { reason: "range"; reign: { name: string }; first: number; last: number }
): string {
  if (error.reason === "reign") {
    return t(strings.errorReign, { name: error.name });
  }
  if (error.reason === "range") {
    return t(strings.errorRange, {
      reign: error.reign.name,
      first: error.first,
      last: error.last,
    });
  }
  return strings.errorFormat;
}

function dateErrorText(strings: Strings, reason: string): string {
  switch (reason) {
    case "invalid":
      return strings.errorInvalid;
    case "lost":
      return strings.errorLost;
    case "dual":
      return strings.errorDual;
    case "before":
      return strings.errorBefore;
    default:
      return strings.errorDateFormat;
  }
}

function Notes({ keys, strings }: { keys: YearNote[]; strings: Strings }) {
  if (keys.length === 0) {
    return null;
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
      {keys.map((key) => (
        <li key={key}>{strings.notes[key]}</li>
      ))}
    </ul>
  );
}

function DayBlock({
  label,
  day,
  strings,
}: {
  label: string;
  day: { written: string; gregorian: string | null; julian: boolean };
  strings: Strings;
}) {
  const written = day.julian
    ? t(strings.oldStyle, { date: day.written })
    : day.written;
  return (
    <div className="space-y-1">
      <p className="font-medium text-foreground text-sm">{label}</p>
      <p className="text-foreground">{written}</p>
      {day.gregorian ? (
        <p className="text-muted-foreground text-sm">
          {strings.gregorianLabel}: {day.gregorian}
        </p>
      ) : null}
    </div>
  );
}

function ReignYearsTable({
  reignId,
  strings,
  format,
}: {
  reignId: string;
  strings: Strings;
  format: DateFormat;
}) {
  const reign = REIGNS.find((item) => item.id === reignId);
  if (!reign) {
    return null;
  }
  const rows: string[][] = [];
  for (let number = 1; number <= 120; number++) {
    const year = regnalYear(reign, number);
    if (year) {
      rows.push([
        year.label,
        formatLegalDay(year.start, format).written,
        formatLegalDay(year.end, format).written,
      ]);
    }
  }
  return (
    <details className={CARD}>
      <summary className="cursor-pointer font-medium text-foreground text-sm">
        {t(strings.reignYearsSummary, { reign: reign.name })}
      </summary>
      <div className="mt-3">
        <DataTable
          head={[strings.tableYear, strings.startsLabel, strings.endsLabel]}
          rows={rows}
        />
      </div>
    </details>
  );
}

function ReferenceTable({
  strings,
  format,
}: {
  strings: Strings;
  format: DateFormat;
}) {
  const rows = REIGNS.map((reign) => {
    const span = reignSpan(reign);
    return [
      reign.name,
      reign.abbr,
      formatLegalDay(span.start, format).written,
      span.end === null
        ? strings.ongoing
        : formatLegalDay(span.end, format).written,
    ];
  });
  return (
    <details className={CARD}>
      <summary className="cursor-pointer font-medium text-foreground text-sm">
        {strings.tableSummary}
      </summary>
      <div className="mt-3">
        <DataTable
          head={[
            strings.tableReign,
            strings.tableAbbr,
            strings.tableFirstYear,
            strings.tableLastDay,
          ]}
          rows={rows}
        />
      </div>
    </details>
  );
}

export default function RegnalYearConverterWidget() {
  const strings = useToolUi(REGNAL_YEAR_CONVERTER_STRINGS);
  const [mode, setMode] = useState<Mode>("toDates");
  const [citation, setCitation] = useState("");
  const [date, setDate] = useState("");
  const [calendar, setCalendar] = useState<CalendarMode>("england");
  const format = useMemo(() => dateFormat(strings), [strings]);
  const datesResult = useMemo(
    () => regnalToDates(citation, format),
    [citation, format]
  );
  const yearResult = useMemo(
    () => dateToRegnal(date, calendar, format),
    [date, calendar, format]
  );

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="regnal-mode"
          onChange={setMode}
          options={[
            { value: "toDates", label: strings.modeToDates },
            { value: "toYear", label: strings.modeToYear },
          ]}
          value={mode}
        />
        {mode === "toDates" ? (
          <TextField
            hint={strings.citationHint}
            id="regnal-citation"
            label={strings.citationLabel}
            onChange={setCitation}
            placeholder={strings.citationPlaceholder}
            value={citation}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.dateHint}
              id="regnal-date"
              label={strings.dateLabel}
              onChange={setDate}
              placeholder={strings.datePlaceholder}
              value={date}
            />
            <SelectField
              id="regnal-calendar"
              label={strings.calendarLabel}
              onChange={setCalendar}
              options={[
                { value: "england", label: strings.calendarEngland },
                { value: "gregorian", label: strings.calendarGregorian },
              ]}
              value={calendar}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              mode === "toDates"
                ? setCitation(strings.exampleCitation)
                : setDate(strings.exampleDate)
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={mode === "toDates" ? citation === "" : date === ""}
            onClick={() => (mode === "toDates" ? setCitation("") : setDate(""))}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {mode === "toDates" ? (
          <DatesResult format={format} result={datesResult} strings={strings} />
        ) : (
          <YearResult format={format} result={yearResult} strings={strings} />
        )}
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <ReferenceTable format={format} strings={strings} />
      </section>
    </div>
  );
}

function DatesResult({
  result,
  strings,
  format,
}: {
  result: ReturnType<typeof regnalToDates>;
  strings: Strings;
  format: DateFormat;
}) {
  if (result.status === "empty") {
    return <Notice>{strings.emptyPromptToDates}</Notice>;
  }
  if (result.status === "error") {
    return <ErrorNotice>{errorText(strings, result)}</ErrorNotice>;
  }
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard emphasis label={strings.regnalYear} value={result.citation} />
        <StatCard label={strings.withoutStops} value={result.plainCitation} />
        <StatCard label={strings.calendarYears} value={result.years} />
      </StatGrid>
      <div className={`${CARD} grid gap-4 sm:grid-cols-2`}>
        <DayBlock
          day={result.start}
          label={strings.startsLabel}
          strings={strings}
        />
        <DayBlock
          day={result.end}
          label={strings.endsLabel}
          strings={strings}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.summary, {
          citation: result.citation,
          start: result.start.full,
          end: result.end.full,
        })}
      />
      {result.partial ? (
        <p className="text-muted-foreground text-sm">{strings.partialYear}</p>
      ) : null}
      <Notes keys={result.notes} strings={strings} />
      <p className="text-muted-foreground text-sm">{strings.sessionNote}</p>
      {result.start.julian ? (
        <p className="text-muted-foreground text-sm">{strings.oldStyleNote}</p>
      ) : null}
      <ReignYearsTable
        format={format}
        reignId={result.reign.id}
        strings={strings}
      />
    </div>
  );
}

function YearResult({
  result,
  strings,
  format,
}: {
  result: ReturnType<typeof dateToRegnal>;
  strings: Strings;
  format: DateFormat;
}) {
  if (result.status === "empty") {
    return <Notice>{strings.emptyPromptToYear}</Notice>;
  }
  if (result.status === "error") {
    return <ErrorNotice>{dateErrorText(strings, result.reason)}</ErrorNotice>;
  }
  if (result.status === "interregnum") {
    return (
      <div className="space-y-3">
        <Notice>{strings.interregnum}</Notice>
        <DayBlock day={result.day} label={strings.dayLabel} strings={strings} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard emphasis label={strings.regnalYear} value={result.citation} />
        <StatCard label={strings.withoutStops} value={result.plainCitation} />
        <StatCard label={strings.reign} value={result.reign.name} />
      </StatGrid>
      <div className={`${CARD} grid gap-4 sm:grid-cols-3`}>
        <DayBlock day={result.day} label={strings.dayLabel} strings={strings} />
        <DayBlock
          day={result.start}
          label={strings.startsLabel}
          strings={strings}
        />
        <DayBlock
          day={result.end}
          label={strings.endsLabel}
          strings={strings}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.summaryDay, {
          day: result.day.full,
          citation: result.citation,
          start: result.start.full,
          end: result.end.full,
        })}
      />
      <Notes keys={result.notes} strings={strings} />
      <p className="text-muted-foreground text-sm">{strings.sessionNote}</p>
      {result.day.julian ? (
        <p className="text-muted-foreground text-sm">{strings.oldStyleNote}</p>
      ) : null}
      <ReignYearsTable
        format={format}
        reignId={result.reign.id}
        strings={strings}
      />
    </div>
  );
}
